import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const RETRIABLE_STATUS_CODES = new Set([408, 425, 429, 500, 502, 503, 504]);

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  timeout: 15000,
  headers: {
    "Content-Type": "application/json",
  },
});

const sleep = (ms) => new Promise((resolve) => globalThis.setTimeout(resolve, ms));

const shouldRetryRequest = (error) => {
  if (axios.isCancel?.(error)) {
    return false;
  }
  if (!error?.response) {
    return true;
  }
  return RETRIABLE_STATUS_CODES.has(error.response.status);
};

const requestWithRetry = async (requestFactory, { retries = 2, baseDelayMs = 900 } = {}) => {
  let attempt = 0;
  while (true) {
    try {
      return await requestFactory();
    } catch (error) {
      if (attempt >= retries || !shouldRetryRequest(error)) {
        throw error;
      }
      attempt += 1;
      await sleep(baseDelayMs * attempt);
    }
  }
};

const normalizeLatestDeck = (startup = {}) => {
  const latestDeckSource =
    typeof startup.latest_deck === "string"
      ? { url: startup.latest_deck }
      : startup.latest_deck || (startup.latest_deck_url ? { url: startup.latest_deck_url } : null);

  const latestDeckUrl = latestDeckSource?.url || startup.latest_deck_url || null;
  if (!latestDeckUrl) {
    return null;
  }

  return {
    ...latestDeckSource,
    url: latestDeckUrl,
    title: latestDeckSource?.title || latestDeckSource?.deck_title || startup.latest_deck_title || startup.name || "Latest deck",
  };
};

const normalizeDeckUrls = (startup = {}, latestDeck = null) => {
  const deckUrls = Array.isArray(startup.deck_urls) ? [...startup.deck_urls] : [];
  const latestDeckUrl = latestDeck?.url || null;

  if (latestDeckUrl && !deckUrls.includes(latestDeckUrl)) {
    deckUrls.push(latestDeckUrl);
  }

  return deckUrls;
};

const normalizePerson = (person = {}) => {
  const sourceIds = Array.isArray(person.source_ids) ? person.source_ids.filter(Boolean) : [];
  const trust = person.trust && typeof person.trust === "object"
    ? {
        ...person.trust,
        confidence: Number.isFinite(person.trust.confidence) ? person.trust.confidence : null,
      }
    : null;

  return {
    ...person,
    title: person.title || null,
    linkedin_url: person.linkedin_url || null,
    twitter_url: person.twitter_url || null,
    github_url: person.github_url || null,
    bio: person.bio || null,
    background: person.background || null,
    role_type: person.role_type || "other",
    source_ids: sourceIds,
    source_count: Number.isFinite(person.source_count) ? person.source_count : sourceIds.length,
    trust,
  };
};

const normalizeFundingRound = (round) => {
  if (!round || typeof round !== "object") {
    return null;
  }

  return {
    ...round,
    round_type: round.round_type || null,
    amount: Number.isFinite(round.amount) ? round.amount : null,
    investors: Array.isArray(round.investors) ? round.investors.filter(Boolean) : [],
    date: round.date || null,
    date_precision: round.date_precision || null,
    confidence: Number.isFinite(round.confidence) ? round.confidence : null,
    source: round.source || null,
  };
};

const normalizeTrustSummary = (trustSummary) => {
  if (!trustSummary || typeof trustSummary !== "object") {
    return null;
  }

  return {
    ...trustSummary,
    confidence: Number.isFinite(trustSummary.confidence) ? trustSummary.confidence : null,
    confidence_percent: Number.isFinite(trustSummary.confidence_percent) ? trustSummary.confidence_percent : null,
    signals: Array.isArray(trustSummary.signals)
      ? trustSummary.signals.filter(Boolean).map((signal) => ({
          key: signal?.key || null,
          label: signal?.label || "",
        }))
      : [],
  };
};

const normalizeStartup = (startup) => {
  if (!startup || typeof startup !== "object") {
    return startup;
  }

  const latestDeck = normalizeLatestDeck(startup);
  const founders = Array.isArray(startup.founders) ? startup.founders.map(normalizePerson) : [];
  const startupPeople = Array.isArray(startup.startup_people) ? startup.startup_people.map(normalizePerson) : [];
  const founderPreview = Array.isArray(startup.founder_preview)
    ? startup.founder_preview.filter(Boolean).map((person) => ({
        name: person?.name || null,
        title: person?.title || null,
        linkedin_url: person?.linkedin_url || null,
      }))
    : founders.slice(0, 2).map((founder) => ({
        name: founder.name,
        title: founder.title,
        linkedin_url: founder.linkedin_url,
      }));
  const latestFundingRound = normalizeFundingRound(startup.latest_funding_round);
  const foundersCount = Number.isFinite(startup.founders_count)
    ? startup.founders_count
    : founders.length > 0
      ? founders.length
      : startupPeople.filter((person) => person.role_type === "founder").length || 0;

  return {
    ...startup,
    city: startup.city || null,
    website: startup.website || null,
    team_page_url: startup.team_page_url || null,
    careers_url: startup.careers_url || null,
    latest_deck: latestDeck,
    deck_urls: normalizeDeckUrls(startup, latestDeck),
    founders,
    founder_preview: founderPreview,
    startup_people: startupPeople,
    founders_count: foundersCount,
    latest_funding_round: latestFundingRound,
    last_funding_date: latestFundingRound?.date || startup.last_funding_date || null,
    trust_summary: normalizeTrustSummary(startup.trust_summary),
    score_drilldown_available: Boolean(
      startup.score_drilldown_available
      ?? startup.conviction_score?.explanations?.analysis_run_id
      ?? startup.conviction_score?.total_score
    ),
  };
};

const normalizeStartupListPayload = (data = {}) => ({
  ...data,
  startups: Array.isArray(data.startups) ? data.startups.map(normalizeStartup) : [],
});

const normalizeFeedPayload = (data = {}) => ({
  ...data,
  sections: Array.isArray(data.sections)
    ? data.sections.map((section) => ({
        ...section,
        startups: Array.isArray(section.startups) ? section.startups.map(normalizeStartup) : [],
      }))
    : [],
  top_startups: Array.isArray(data.top_startups) ? data.top_startups.map(normalizeStartup) : [],
});

const normalizeScoreDrilldown = (data = {}) => ({
  ...data,
  ranking_summary: data.ranking_summary && typeof data.ranking_summary === "object"
    ? {
        ...data.ranking_summary,
        confidence: Number.isFinite(data.ranking_summary.confidence) ? data.ranking_summary.confidence : null,
        key_strengths: Array.isArray(data.ranking_summary.key_strengths) ? data.ranking_summary.key_strengths : [],
        key_risks: Array.isArray(data.ranking_summary.key_risks) ? data.ranking_summary.key_risks : [],
        metric_scores: data.ranking_summary.metric_scores && typeof data.ranking_summary.metric_scores === "object"
          ? data.ranking_summary.metric_scores
          : {},
      }
    : null,
  metrics: data.metrics && typeof data.metrics === "object" ? data.metrics : {},
  agent_reasoning: Array.isArray(data.agent_reasoning)
    ? data.agent_reasoning.map((artifact) => ({
        ...artifact,
        evidence: Array.isArray(artifact.evidence) ? artifact.evidence : [],
        details: artifact.details && typeof artifact.details === "object" ? artifact.details : {},
      }))
    : [],
});

const normalizeResponse = async (requestPromise, normalizer = (data) => data) => {
  const response = await requestPromise;
  response.data = normalizer(response.data);
  return response;
};

export const authApi = {
  getConfig: () => api.get("/auth/config"),
  devLogin: (data) => api.post("/auth/dev-login", data),
  passcodeLogin: (data) => api.post("/auth/passcode-login", data),
  exchangeSession: (sessionId) => api.post("/auth/session", { session_id: sessionId }),
  getMe: () => api.get("/auth/me"),
  logout: () => api.post("/auth/logout"),
  updatePreferences: (data) => api.put("/auth/preferences", data),
};

export const startupApi = {
  list: (params) => normalizeResponse(requestWithRetry(() => api.get("/startups/", { params })), normalizeStartupListPayload),
  create: (data) => normalizeResponse(api.post("/startups/", data), normalizeStartup),
  get: (id) => normalizeResponse(requestWithRetry(() => api.get(`/startups/${id}`)), normalizeStartup),
  getTop: (limit = 10) => normalizeResponse(requestWithRetry(() => api.get("/startups/top", { params: { limit } })), normalizeStartupListPayload),
  getSignals: (limit = 20) => api.get("/startups/signals", { params: { limit } }),
  enrich: (id) => normalizeResponse(api.post(`/startups/${id}/enrich`), normalizeStartup),
  runDiligence: (id) => api.post(`/startups/${id}/diligence`),
  getDiligenceReport: (id) => normalizeResponse(requestWithRetry(() => api.get(`/startups/${id}/diligence-report`)), normalizeScoreDrilldown),
  score: (id) => api.post(`/startups/${id}/score`),
  getScoreDrilldown: (id) => normalizeResponse(requestWithRetry(() => api.get(`/startups/${id}/score-drilldown`)), normalizeScoreDrilldown),
  generateMemo: (id) => api.post(`/startups/${id}/memo`),
  addSignal: (id, signal) => api.post(`/startups/${id}/signals`, signal),
  addToWatchlist: (id, watchlistName = "default") => api.post(`/startups/${id}/watchlist`, null, { params: { watchlist_name: watchlistName } }),
};

export const dealRoomApi = {
  list: (status) => api.get("/deal-rooms/", { params: { status } }),
  create: (data) => api.post("/deal-rooms/", data),
  get: (id) => api.get(`/deal-rooms/${id}`),
  uploadDocument: (id, data) => api.post(`/deal-rooms/${id}/documents`, data),
  ingestUrl: (id, url) => api.post(`/deal-rooms/${id}/ingest-url`, { url }),
  getDocuments: (id) => api.get(`/deal-rooms/${id}/documents`),
  generateDashboard: (id) => api.post(`/deal-rooms/${id}/dashboard`),
  chat: (id, message) => api.post(`/deal-rooms/${id}/chat`, { message }),
  getChatHistory: (id, limit = 50) => api.get(`/deal-rooms/${id}/chat`, { params: { limit } }),
};

export const investorApi = {
  list: (params) => api.get("/investors/", { params }),
  create: (data) => api.post("/investors/", data),
  get: (id) => api.get(`/investors/${id}`),
  listFunds: (params) => api.get("/investors/funds", { params }),
  createFund: (data) => api.post("/investors/funds", data),
  findFit: (startupId, limit = 20) => api.post(`/investors/fit/${startupId}`, null, { params: { limit } }),
  getRelationships: (personType) => api.get("/investors/relationships/", { params: { person_type: personType } }),
  createRelationship: (data) => api.post("/investors/relationships/", data),
  findIntroPaths: (investorId) => api.get(`/investors/intro-paths/${investorId}`),
};

export const thesisApi = {
  list: () => api.get("/theses/"),
  create: (data) => api.post("/theses/", data),
  get: (id) => api.get(`/theses/${id}`),
  analyze: (id) => api.post(`/theses/${id}/analyze`),
  source: (id, params = {}) => api.post(`/theses/${id}/source`, null, {
    params: { top_n: params.topN || 50, deep_n: params.deepN || 10 },
    timeout: 120000, // 2min for AI sourcing
  }),
  getSourced: (id) => api.get(`/theses/${id}/sourced`),
  linkStartup: (thesisId, startupId) => api.post(`/theses/${thesisId}/link/${startupId}`),
  delete: (id) => api.delete(`/theses/${id}`),
};

export const outreachApi = {
  listDrafts: (status) => api.get("/outreach/drafts", { params: { status } }),
  createDraft: (data) => api.post("/outreach/drafts", data),
  getDraft: (id) => api.get(`/outreach/drafts/${id}`),
  updateDraft: (id, data) => api.put(`/outreach/drafts/${id}`, data),
  deleteDraft: (id) => api.delete(`/outreach/drafts/${id}`),
  listThreads: (status) => api.get("/outreach/threads", { params: { status } }),
  getThread: (id) => api.get(`/outreach/threads/${id}`),
};

export const dashboardApi = {
  getBrief: (includeReviewed = false) => normalizeResponse(requestWithRetry(() => api.get("/dashboard/brief", { params: { include_reviewed: includeReviewed } })), normalizeFeedPayload),
  getFeed: (includeReviewed = false) => normalizeResponse(api.get("/feed/brief", { params: { include_reviewed: includeReviewed } }), normalizeFeedPayload),
  markStartupSeen: (startupId) => api.post(`/feed/startups/${startupId}/seen`),
  setStartupState: (startupId, state) => api.post(`/feed/startups/${startupId}/state`, { state }),
  getProviderStatus: () => requestWithRetry(() => api.get("/providers/status")),
  healthCheck: () => requestWithRetry(() => api.get("/health")),
};

export const watchlistApi = {
  get: (watchlistName = "default") => normalizeResponse(api.get("/watchlists/", { params: { watchlist_name: watchlistName } }), normalizeStartupListPayload),
  add: (startupId, watchlistName = "default") => api.post(`/watchlists/${startupId}`, null, { params: { watchlist_name: watchlistName } }),
  remove: (startupId, watchlistName = "default") => api.delete(`/watchlists/${startupId}`, { params: { watchlist_name: watchlistName } }),
  check: (startupId, watchlistName = "default") => api.get(`/watchlists/check/${startupId}`, { params: { watchlist_name: watchlistName } }),
};

export const stealthApi = {
  list: (params) => api.get("/stealth/", { params }),
};

export const vcFundsApi = {
  list: (params) => api.get("/vc-funds/", { params }),
  get: (fundName) => api.get(`/vc-funds/${encodeURIComponent(fundName)}`),
};

// Diligence agents — uses native fetch for SSE streaming, axios for REST
export const diligenceAgentsApi = {
  // Returns native fetch Response for SSE streaming; caller reads body as a stream
  runStream: (startupId) =>
    fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/api/startups/${startupId}/diligence-agents/run`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    }),
  getLatest: (startupId) =>
    api.get(`/startups/${startupId}/diligence-agents/latest`),
  chat: (startupId, agentType, message, sessionId) =>
    api.post(`/startups/${startupId}/diligence-agents/${agentType}/chat`, {
      message,
      session_id: sessionId || null,
    }),
  getChatHistory: (startupId, agentType) =>
    api.get(`/startups/${startupId}/diligence-agents/${agentType}/chat`),
};

export default api;
